import React, { useRef, useState } from 'react';
import { Box, ListItem, Typography, LinearProgress, IconButton, TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { 
  DragIndicator as DragIcon, 
  Edit as EditIcon, 
  Check as CheckIcon, 
  Close as CloseIcon,
  Balance as BalanceIcon,
  Delete as DeleteIcon 
} from '@mui/icons-material';
import { useDrag, useDrop } from 'react-dnd';
import type { DragSourceMonitor, DropTargetMonitor } from 'react-dnd';
import { alpha, Theme } from '@mui/material/styles';
import { Budget, Transaction } from '../../types';
import { useTransactions } from '../../contexts/TransactionContext';
import { useBudget } from '../../contexts/BudgetContext';
import { useNavigation } from '../../contexts/NavigationContext';
import type { Change } from '../../contexts/NavigationContext';
import { budgets } from '../../services/api';

const ItemTypes = {
  TRANSACTION: 'transaction' as const,
  CATEGORY: 'category' as const
};

interface CategoryDragItem {
  id: string;
  index: number;
  type: 'income' | 'expense' | 'savings';
}

interface DroppableCategoryProps {
  category: Budget['categories'][0];
  index: number;
  onDrop: (transaction: Transaction) => Promise<void>;
  onMove: (dragIndex: number, hoverIndex: number, type: 'income' | 'expense' | 'savings') => void;
  onDelete?: (category: Budget['categories'][0]) => Promise<void>;
}

export const DroppableCategory: React.FC<DroppableCategoryProps> = React.memo(({ 
  category, 
  index,
  onDrop,
  onMove,
  onDelete
}) => {
  const ref = useRef<HTMLLIElement>(null);
  const { categoryFilter, setCategoryFilter } = useTransactions();
  const { currentBudget, setCurrentBudget } = useBudget();
  const { setChangeHistory, setUndoneChanges } = useNavigation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedAmount, setEditedAmount] = useState(category.budgetedAmount.toString());
  const [showEditButton, setShowEditButton] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Calculate toBeBudgeted
  const toBeBudgeted = React.useMemo(() => {
    if (!currentBudget) return 0;
    
    const totalIncome = currentBudget.categories
      .filter(cat => cat.type === 'income')
      .reduce((sum, cat) => sum + cat.actualAmount, 0);
    
    // "To be budgeted" = total income + rollover from previous months - amounts assigned to categories
    const rolloverAmount = currentBudget.toBeBudgetedRollover || 0;
    return totalIncome + rolloverAmount - currentBudget.totalBudgeted;
  }, [currentBudget]);

  const handleDoubleClick = () => {
    if (categoryFilter === category.name) {
      setCategoryFilter(null); // Clear filter if clicking the same category
    } else {
      setCategoryFilter(category.name); // Set filter to this category
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedAmount(category.budgetedAmount.toString());
  };

  const handleSave = async () => {
    const newAmount = parseFloat(editedAmount);
    if (isNaN(newAmount) || newAmount < 0) return;

    if (currentBudget) {
      const oldAmount = category.budgetedAmount;
      const updatedCategories = currentBudget.categories.map(cat => 
        cat.name === category.name && cat.type === category.type
          ? { ...cat, budgetedAmount: newAmount }
          : cat
      );

      const updatedBudget = {
        ...currentBudget,
        categories: updatedCategories,
        totalBudgeted: currentBudget.totalBudgeted - oldAmount + newAmount
      };

      try {
        // Update in the database
        await budgets.update(currentBudget._id, {
          categories: updatedCategories,
          totalBudgeted: currentBudget.totalBudgeted - oldAmount + newAmount
        });

        // Add to change history
        const change: Change = {
          type: 'budget_amount',
          categoryName: category.name,
          categoryType: category.type,
          oldAmount,
          newAmount,
          timestamp: Date.now()
        };

        setChangeHistory(prev => [...prev, change]);
        setUndoneChanges([]);
        setCurrentBudget(updatedBudget);
      } catch (error) {
        console.error('Error updating budget:', error);
      }
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedAmount(category.budgetedAmount.toString());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBalance = async () => {
    if (!currentBudget || category.type !== 'expense') return;

    const deficit = category.actualAmount - category.budgetedAmount;
    if (deficit <= 0) return;

    const oldAmount = category.budgetedAmount;
    // Use either the full actual amount or as much as we can from toBeBudgeted
    const newAmount = toBeBudgeted >= deficit 
      ? category.actualAmount 
      : category.budgetedAmount + toBeBudgeted;

    const updatedCategories = currentBudget.categories.map(cat => 
      cat.name === category.name && cat.type === category.type
        ? { ...cat, budgetedAmount: newAmount }
        : cat
    );

    const updatedBudget = {
      ...currentBudget,
      categories: updatedCategories,
      totalBudgeted: currentBudget.totalBudgeted - oldAmount + newAmount
    };

    try {
      // Update in the database
      await budgets.update(currentBudget._id, {
        categories: updatedCategories,
        totalBudgeted: currentBudget.totalBudgeted - oldAmount + newAmount
      });

      // Add to change history
      const change: Change = {
        type: 'budget_amount',
        categoryName: category.name,
        categoryType: category.type,
        oldAmount,
        newAmount,
        timestamp: Date.now()
      };

      setChangeHistory(prev => [...prev, change]);
      setUndoneChanges([]);
      setCurrentBudget(updatedBudget);
    } catch (error) {
      console.error('Error updating budget:', error);
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!currentBudget || !onDelete) return;

    try {
      await onDelete(category);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  const [{ handlerOver, canDrop }, drop] = useDrop<
    Transaction | CategoryDragItem,
    { category: string },
    { handlerOver: boolean; canDrop: boolean }
  >(() => ({
    accept: [ItemTypes.TRANSACTION, ItemTypes.CATEGORY],
    drop: (item: Transaction | CategoryDragItem) => {
      if ('date' in item) { // Transaction item
        onDrop(item);
        return { category: category.name };
      }
      return undefined;
    },
    canDrop: (item: Transaction | CategoryDragItem) => {
      if ('date' in item) { // Transaction item
        return item.type === category.type && item.category !== category.name;
      }
      return item.type === category.type;
    },
    hover: (item: Transaction | CategoryDragItem, monitor: DropTargetMonitor) => {
      if (!ref.current || !('index' in item)) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      onMove(dragIndex, hoverIndex, category.type);
      item.index = hoverIndex;
    },
    collect: (monitor: DropTargetMonitor) => ({
      handlerOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [category.name, category.type, index, onDrop, onMove]);

  const [{ categoryDragging }, drag] = useDrag<
    CategoryDragItem,
    unknown,
    { categoryDragging: boolean }
  >(() => ({
    type: ItemTypes.CATEGORY,
    item: { id: category.name, index, type: category.type },
    collect: (monitor: DragSourceMonitor) => ({
      categoryDragging: monitor.isDragging()
    })
  }), [category.name, index, category.type]);

  drag(drop(ref));

  const isDragging = categoryDragging || handlerOver;

  // Type guard functions
  const isIncomeCategory = (type: 'income' | 'expense' | 'savings'): type is 'income' => type === 'income';
  const isExpenseCategory = (type: 'income' | 'expense' | 'savings'): type is 'expense' => type === 'expense';
  const isSavingsCategory = (type: 'income' | 'expense' | 'savings'): type is 'savings' => type === 'savings';

  // Memoize the formatted values
  const formattedActualAmount = React.useMemo(() => 
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(category.actualAmount)
  , [category.actualAmount]);

  const formattedBudgetedAmount = React.useMemo(() => 
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(category.budgetedAmount)
  , [category.budgetedAmount]);

  const progress = React.useMemo(() => {
    if (category.budgetedAmount === 0) return 0;
    
    // For savings categories, invert the progress logic
    if (isSavingsCategory(category.type)) {
      // Savings progress: negative when under goal, positive when over goal
      const savingsProgress = ((category.actualAmount - category.budgetedAmount) / category.budgetedAmount) * 100;
      return savingsProgress;
    }
    
    // For expense categories, use normal progress calculation
    return (category.actualAmount / category.budgetedAmount) * 100;
  }, [category.actualAmount, category.budgetedAmount, category.type]);

  const progressColor = React.useMemo(() => {
    const percentage = (category.actualAmount / category.budgetedAmount) * 100;
    if (percentage > 100) return 'error';
    if (percentage === 100) return 'success';
    if (percentage >= 90) return 'warning';
    return 'primary';
  }, [category.actualAmount, category.budgetedAmount]);

  const getProgressSx = React.useMemo(() => {
    return {
      height: 8,
      borderRadius: 4,
      backgroundColor: (theme: Theme) => theme.palette.mode === 'light' ? '#E4E8ED' : '#2D2D2D',
      '& .MuiLinearProgress-bar': {
        borderRadius: 4,
      }
    };
  }, [category.actualAmount, category.budgetedAmount, category.type]);

  const isOverBudget = category.actualAmount > category.budgetedAmount;

  return (
    <>
      <ListItem
        ref={ref}
        onDoubleClick={handleDoubleClick}
        onMouseEnter={() => setShowEditButton(true)}
        onMouseLeave={() => setShowEditButton(false)}
        sx={{
          flexDirection: 'column',
          alignItems: 'stretch',
          backgroundColor: isDragging ? (
            handlerOver && canDrop 
              ? 'action.hover'
              : handlerOver && !canDrop 
                ? 'error.light' 
                : 'transparent'
          ) : categoryFilter === category.name ? alpha('#1976d2', 0.1) : 'transparent',
          opacity: isDragging ? 0.5 : 1,
          transition: 'all 0.2s ease',
          cursor: 'move',
          borderRadius: 1,
          my: 0.5,
          p: 1,
          ...(isDragging && {
            outline: canDrop
              ? handlerOver
                ? '2px dashed rgba(0, 0, 0, 0.4)'
                : '2px dashed rgba(0, 0, 0, 0.2)'
              : handlerOver
                ? '2px dashed #d32f2f'
                : 'none'
          })
        }}
      >
        <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <DragIcon sx={{ color: 'text.secondary', cursor: 'move' }} />
            <Typography 
              variant="body1" 
              color={isDragging && handlerOver && !canDrop ? 'error.main' : 'text.primary'}
            >
              {category.name}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {showEditButton && (
              <Tooltip title="Delete category">
                <IconButton 
                  size="small" 
                  onClick={handleDeleteClick}
                  sx={{ 
                    opacity: 0.7,
                    padding: '2px',
                    '& .MuiSvgIcon-root': {
                      fontSize: '0.875rem'
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            <Typography 
              variant="body1" 
              color={isIncomeCategory(category.type)
                ? 'success.main'
                : isSavingsCategory(category.type)
                  ? (category.actualAmount >= category.budgetedAmount ? 'success.main' : 'primary.main')
                  : progressColor === 'error' ? 'error.main' : progressColor === 'warning' ? 'warning.main' : 'primary.main'}
              sx={{ fontWeight: 'medium' }}
            >
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
              }).format(isIncomeCategory(category.type)
                ? category.actualAmount
                : isSavingsCategory(category.type)
                  ? category.actualAmount - category.budgetedAmount
                  : category.budgetedAmount - category.actualAmount)}
            </Typography>
          </Box>
        </Box>
        {(isExpenseCategory(category.type) || isSavingsCategory(category.type)) && (
          <Box width="100%" sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={
                isSavingsCategory(category.type)
                  ? Math.max(0, Math.min(progress + 100, 100)) // For savings: convert range from [-100,+∞] to [0,100]
                  : isOverBudget ? 100 : Math.min(progress, 100) // For expenses: normal logic
              }
              color={isSavingsCategory(category.type) ? 'primary' : progressColor}
              sx={getProgressSx}
            />
            <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%" mt={0.5}>
              {isEditing ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    size="small"
                    value={editedAmount}
                    onChange={(e) => setEditedAmount(e.target.value)}
                    onKeyDown={handleKeyPress}
                    type="number"
                    inputProps={{ 
                      min: 0, 
                      step: "0.01",
                      style: { textAlign: 'right' }
                    }}
                    autoFocus
                    sx={{ 
                      width: '100px',
                      '& .MuiInputBase-root': {
                        height: '24px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '0px 8px',
                        fontSize: '0.75rem',
                        height: '24px',
                      }
                    }}
                  />
                  <IconButton 
                    size="small" 
                    onClick={handleSave} 
                    color="primary"
                    sx={{ 
                      padding: '2px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={handleCancel}
                    sx={{ 
                      padding: '2px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ) : (
                <>
                  {showEditButton && (isExpenseCategory(category.type) || isSavingsCategory(category.type)) && (
                    <>
                      {isOverBudget && toBeBudgeted > 0 && isExpenseCategory(category.type) && (
                        <Tooltip title={toBeBudgeted >= (category.actualAmount - category.budgetedAmount) 
                          ? "Balance this category using To Be Budgeted"
                          : `Assign ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(toBeBudgeted)} from To Be Budgeted`
                        }>
                          <IconButton 
                            size="small" 
                            onClick={handleBalance}
                            color="primary"
                            sx={{ 
                              opacity: 0.7, 
                              mr: 1,
                              padding: '2px',
                              '& .MuiSvgIcon-root': {
                                fontSize: '0.875rem'
                              }
                            }}
                          >
                            <BalanceIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={`Edit ${isSavingsCategory(category.type) ? 'savings goal' : 'budget amount'}`}>
                        <IconButton 
                          size="small" 
                          onClick={handleEditClick}
                          sx={{ 
                            opacity: 0.7, 
                            mr: 1,
                            padding: '2px',
                            '& .MuiSvgIcon-root': {
                              fontSize: '0.875rem'
                            }
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Typography 
                    variant="caption" 
                    color={
                      isSavingsCategory(category.type) 
                        ? (isOverBudget ? 'success.main' : 'text.secondary')
                        : (isOverBudget ? 'error.main' : 'text.secondary')
                    }
                    sx={{ fontWeight: isOverBudget ? 'bold' : 'normal' }}
                  >
                    {formattedActualAmount} / {formattedBudgetedAmount}
                    {isSavingsCategory(category.type) && isOverBudget && " ✓"}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        )}
      </ListItem>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the category "{category.name}"? This action cannot be undone.
            {category.actualAmount > 0 && (
              <>
                <br /><br />
                Warning: This category has transactions associated with it. Deleting the category will permanently delete all associated transactions.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}); 